import * as THREE from "three";
import { GLTFLoader, type GLTF } from "three/addons/loaders/GLTFLoader.js";
import {
  findAsset,
  resolveAssetUrl,
  selectLod,
  type AssetManifest,
  type GltfAssetDefinition,
  type PbrAssetDefinition,
  type TextureMapKind,
} from "./manifest";

export interface StaticAssetLease {
  object: THREE.Object3D;
  release(): void;
}

export interface MaterialAssetLease {
  material: THREE.MeshStandardMaterial;
  release(): void;
}

export interface AssetRuntimeLoaders {
  loadGltf(url: string): Promise<GLTF>;
  loadTexture(url: string): Promise<THREE.Texture>;
}

export interface AcquireStaticOptions {
  distance?: number;
  variant?: string;
}

interface CacheEntry<T> {
  promise: Promise<T>;
  references: number;
}

const TEXTURE_PROPERTIES = [
  "map",
  "normalMap",
  "roughnessMap",
  "aoMap",
  "metalnessMap",
  "alphaMap",
  "emissiveMap",
] as const;

export class AssetRuntime {
  private readonly models = new Map<string, CacheEntry<THREE.Object3D>>();
  private readonly materials = new Map<string, CacheEntry<THREE.MeshStandardMaterial>>();
  private readonly leases = new Set<() => void>();
  private disposed = false;

  constructor(
    private readonly manifest: AssetManifest,
    private readonly baseUrl = import.meta.env.BASE_URL,
    private readonly loaders: AssetRuntimeLoaders = createDefaultLoaders(),
  ) {}

  async acquireStatic(id: string, options: AcquireStaticOptions = {}): Promise<StaticAssetLease> {
    this.assertActive();
    const asset = findAsset(this.manifest, id);
    if (asset.kind !== "gltf") throw new Error(`Asset ${id} is not a glTF model`);

    const selected = selectLod(asset, options.distance, options.variant);
    const cacheKey = `${id}:${selected.url}`;
    const entry = this.getOrCreateModel(cacheKey, asset, selected.url);
    entry.references += 1;
    let template: THREE.Object3D;
    try {
      template = await entry.promise;
      this.assertActive();
    } catch (error) {
      entry.references = Math.max(0, entry.references - 1);
      throw error;
    }

    const object = template.clone(true);
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      object.removeFromParent();
      entry.references = Math.max(0, entry.references - 1);
      this.leases.delete(release);
    };
    this.leases.add(release);
    return { object, release };
  }

  async acquireMaterial(id: string): Promise<MaterialAssetLease> {
    this.assertActive();
    const asset = findAsset(this.manifest, id);
    if (asset.kind !== "pbr") throw new Error(`Asset ${id} is not a PBR material`);

    const entry = this.getOrCreateMaterial(id, asset);
    entry.references += 1;
    let material: THREE.MeshStandardMaterial;
    try {
      material = await entry.promise;
      this.assertActive();
    } catch (error) {
      entry.references = Math.max(0, entry.references - 1);
      throw error;
    }

    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      entry.references = Math.max(0, entry.references - 1);
      this.leases.delete(release);
    };
    this.leases.add(release);
    return { material, release };
  }

  evict(id: string): boolean {
    let evicted = false;
    for (const [key, entry] of this.models) {
      if (!key.startsWith(`${id}:`) || entry.references > 0) continue;
      entry.promise.then((object) => disposeObjectResources(object)).catch(() => undefined);
      this.models.delete(key);
      evicted = true;
    }

    const material = this.materials.get(id);
    if (material && material.references === 0) {
      material.promise.then((value) => disposeMaterial(value)).catch(() => undefined);
      this.materials.delete(id);
      evicted = true;
    }
    return evicted;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const release of [...this.leases]) release();
    for (const entry of this.models.values()) {
      entry.promise.then((object) => disposeObjectResources(object)).catch(() => undefined);
    }
    for (const entry of this.materials.values()) {
      entry.promise.then((material) => disposeMaterial(material)).catch(() => undefined);
    }
    this.models.clear();
    this.materials.clear();
  }

  private getOrCreateModel(
    cacheKey: string,
    asset: GltfAssetDefinition,
    relativeUrl: string,
  ): CacheEntry<THREE.Object3D> {
    const cached = this.models.get(cacheKey);
    if (cached) return cached;

    const entry: CacheEntry<THREE.Object3D> = {
      references: 0,
      promise: this.loaders.loadGltf(resolveAssetUrl(this.baseUrl, relativeUrl)).then((gltf) => {
        gltf.scene.userData.assetId = asset.id;
        return gltf.scene;
      }),
    };
    entry.promise.catch(() => this.models.delete(cacheKey));
    this.models.set(cacheKey, entry);
    return entry;
  }

  private getOrCreateMaterial(id: string, asset: PbrAssetDefinition): CacheEntry<THREE.MeshStandardMaterial> {
    const cached = this.materials.get(id);
    if (cached) return cached;

    const entry: CacheEntry<THREE.MeshStandardMaterial> = {
      references: 0,
      promise: this.loadPbrMaterial(asset),
    };
    entry.promise.catch(() => this.materials.delete(id));
    this.materials.set(id, entry);
    return entry;
  }

  private async loadPbrMaterial(asset: PbrAssetDefinition): Promise<THREE.MeshStandardMaterial> {
    const textures = new Map<TextureMapKind, THREE.Texture>();
    try {
      for (const kind of ["baseColor", "normal", "roughness", "ao"] as const) {
        const file = asset.maps[kind];
        if (!file) continue;
        const texture = await this.loaders.loadTexture(resolveAssetUrl(this.baseUrl, file.url));
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(...(asset.repeat ?? [1, 1]));
        texture.colorSpace = kind === "baseColor" ? THREE.SRGBColorSpace : THREE.NoColorSpace;
        textures.set(kind, texture);
      }
    } catch (error) {
      for (const texture of textures.values()) texture.dispose();
      throw error;
    }

    return new THREE.MeshStandardMaterial({
      map: textures.get("baseColor") ?? null,
      normalMap: textures.get("normal") ?? null,
      roughnessMap: textures.get("roughness") ?? null,
      aoMap: textures.get("ao") ?? null,
      roughness: 1,
      metalness: 0,
    });
  }

  private assertActive(): void {
    if (this.disposed) throw new Error("AssetRuntime has been disposed");
  }
}

function createDefaultLoaders(): AssetRuntimeLoaders {
  const gltfLoader = new GLTFLoader();
  const textureLoader = new THREE.TextureLoader();
  return {
    loadGltf: (url) => gltfLoader.loadAsync(url),
    loadTexture: (url) => textureLoader.loadAsync(url),
  };
}

function disposeObjectResources(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    geometries.add(object.geometry);
    const meshMaterials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of meshMaterials) materials.add(material);
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) disposeMaterial(material);
}

function disposeMaterial(material: THREE.Material): void {
  const candidate = material as THREE.Material & Partial<Record<(typeof TEXTURE_PROPERTIES)[number], THREE.Texture | null>>;
  for (const property of TEXTURE_PROPERTIES) candidate[property]?.dispose();
  material.dispose();
}
